import * as React from "react";
import { useEffect } from "react";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { Grid, Box } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { getUserName } from "../Authentication/authSlice";
import LogOutDialog from "./LogoutDialog";
import { WithAnyPermissionGroupTrue } from "../../Global/permissions/checkPermissions";
import Alert from "@mui/material/Alert";
import { useComprehensiveConnectionTest } from "./connectionsTest";
import ConnectionBlockingDialog from "./ConnectionBlockingDialog";
import NotificationImportantOutlinedIcon from "@mui/icons-material/NotificationImportantOutlined";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HomeIcon from "@mui/icons-material/Home";
import NotificationsIcon from "@mui/icons-material/Notifications";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useGetDeclinedJobCardsQuery, useGetInstallerDashboardQuery } from "../api/apiSlice";
import { useClock } from "./useClock";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import { setsteps, setCurrentStep } from "./Stepper/stepperSlice";

const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function getGreetingDate() {
  const d = new Date();
  return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`.toUpperCase();
}

// Maps job state + install_state to { stepIndex, totalSteps, stepLabels }
export function getStepperInfo(jobCard) {
  const { state, install_state } = jobCard;
  if (install_state === "NEW") {
    const labels = ["Details", "QR Scan", "Before", "Install", "Test", "After"];
    const stateMap = { QRC: 1, BEFWA: 2, BEF: 2, AMP: 3, TES: 4, AFT: 5, AFTWA: 5, REF: 5 };
    return { stepIndex: stateMap[state] ?? 0, totalSteps: 6, labels };
  }
  if (install_state === "UN") {
    const labels = ["QR Scan", "Before", "Uninstall", "After"];
    const stateMap = { BEFWA: 1, BEF: 1, UNI: 2, AFT: 3, AFTWA: 3, REF: 3 };
    return { stepIndex: stateMap[state] ?? 0, totalSteps: 4, labels };
  }
  if (install_state === "RE") {
    const labels = ["QR", "Before", "Remove", "New QR", "Details", "Install", "Test", "After"];
    const stateMap = { BEFWA: 1, BEF: 1, UNI: 2, QRCRE: 3, DET: 4, AMP: 5, TES: 6, AFT: 7, AFTWA: 7, REF: 7 };
    return { stepIndex: stateMap[state] ?? 0, totalSteps: 8, labels };
  }
  return { stepIndex: 0, totalSteps: 6, labels: [] };
}

export function getInstallTypeLabel(install_state) {
  if (install_state === "UN") return "Uninstall";
  if (install_state === "RE") return "Replacement";
  return "New Install";
}

export function getStateLabel(state) {
  const labels = {
    QRC: "QR Scan", BEFWA: "Work Area Photos", BEF: "Before Photos",
    AMP: "AMP Test", TES: "Connection Test", UNI: "Uninstalling",
    QRCRE: "QR Scan (New)", DET: "New Device Details",
    AFT: "After Photos", AFTWA: "Work Area After", REF: "Review & Sign Off",
    QEP: "Pending QE Review", COM: "Completed",
  };
  return labels[state] || state;
}

function ConfirmReplaceUninstallDialog({ open, handleClose, type }) {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  let text = type === "UN" ? "UNINSTALL" : "REPLACE";
  const warningMsg = type === "UN"
    ? "You will not be able to cancel an uninstall."
    : "You will not be able to cancel a replacement once started.";

  const handleConfirm = () => {
    if (type === "RE") {
      dispatch(setsteps([0, 1, 2, 3, 4, 5, 6, 7]));
    } else {
      dispatch(setsteps([0, 1, 2, 3]));
    }
    navigate(`/installer/uninstall/${type}/`);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          backgroundColor: "#1A1C24",
          borderRadius: "16px",
          border: "1px solid rgba(255,255,255,0.07)",
          mx: 2,
        },
      }}
    >
      <DialogContent>
        <DialogContentText
          sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", mb: 1.5 }}
        >
          Are you sure you want to <span style={{ color: "#fff" }}>{text}</span> the device?
        </DialogContentText>
        <Box
          sx={{
            backgroundColor: "rgba(253,185,64,0.08)",
            border: "1px solid rgba(253,185,64,0.25)",
            borderRadius: "10px",
            p: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ color: "#FDB940", fontSize: 20, mt: 0.2 }} />
          <Typography sx={{ color: "#FDB940", fontSize: "0.85rem" }}>
            {warningMsg}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          fullWidth
          sx={{
            backgroundColor: "rgba(255,255,255,0.06)",
            color: "#fff",
            borderRadius: "10px",
            fontWeight: 700,
            py: 1.2,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          BACK
        </Button>
        <Button
          onClick={handleConfirm}
          fullWidth
          sx={{
            backgroundColor: "#FDB940",
            color: "#000",
            borderRadius: "10px",
            fontWeight: 700,
            py: 1.2,
            "&:hover": { backgroundColor: "#e6a832" },
          }}
        >
          CONFIRM
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function InstallerLandingPage() {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const user_name = useSelector(getUserName);
  const [open, setOpen] = React.useState(false);
  const [type, setType] = React.useState("");
  const { clockedInTime, handleClockIn } = useClock();

  const {
    data: dashboardData,
    isSuccess: isDashboardSuccess,
    isLoading: isDashboardLoading,
    refetch: refetchDashboard,
  } = useGetInstallerDashboardQuery();

  const {
    isFullyConnected,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    lastChecked,
    connectionDetails,
  } = useComprehensiveConnectionTest();

  const [showConnectionDialog, setShowConnectionDialog] = React.useState(false);

  useEffect(() => {
    if (!isFullyConnected) {
      setShowConnectionDialog(true);
    } else {
      setShowConnectionDialog(false);
    }
  }, [isFullyConnected]);

  const handleRetryConnection = () => {
    refetchDashboard();
  };

  const handleUninstall = () => {
    if (!clockedInTime) return;
    setOpen(true);
    setType("UN");
    dispatch(setCurrentStep(0));
    dispatch(setsteps([0, 1, 2, 3]));
  };

  const handleReplacement = () => {
    if (!clockedInTime) return;
    setOpen(true);
    setType("RE");
    dispatch(setCurrentStep(0));
    dispatch(setsteps([0, 1, 2, 3, 4, 5, 6, 7]));
  };

  const handleNew = () => {
    if (!clockedInTime) return;
    dispatch(setsteps([0, 1, 2, 3, 4, 5]));
    dispatch(setCurrentStep(0));
    localStorage.setItem("clientDetails", true);
    navigate(`/installer/details/`);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleResume = (jobCard) => {
    const ref = jobCard.reference_id;
    const sn = jobCard.serial_number || "";
    const imsi = jobCard.imsi_number || "";
    const archSn = jobCard.archived_serial_number || "";
    const archImsi = jobCard.archived_imsi_number || "";

    if (jobCard.install_state === "NEW") {
      dispatch(setsteps([0, 1, 2, 3, 4, 5]));
      if (jobCard.state === "QRC") {
        dispatch(setCurrentStep(1));
        navigate(`/installer/${ref}/qrcode/`);
      } else if (jobCard.state === "BEFWA" || jobCard.state === "BEF") {
        dispatch(setCurrentStep(2));
        navigate(`/installer/${ref}/${sn}/${imsi}/work_area_before_install/`);
      } else if (jobCard.state === "AMP") {
        dispatch(setCurrentStep(3));
        navigate(`/installer/${ref}/${sn}/${imsi}/connection_test_interlude/`);
      } else if (jobCard.state === "TES") {
        dispatch(setCurrentStep(4));
        navigate(`/installer/${ref}/${sn}/${imsi}/connection_test/`);
      } else if (jobCard.state === "AFT" || jobCard.state === "AFTWA") {
        dispatch(setCurrentStep(5));
        navigate(`/installer/${ref}/${sn}/${imsi}/after_install/`);
      } else if (jobCard.state === "REF") {
        navigate(`/installer/${ref}/${sn}/${imsi}/job_completed/`);
      }
    } else if (jobCard.install_state === "UN") {
      dispatch(setsteps([0, 1, 2, 3]));
      if (jobCard.state === "BEFWA" || jobCard.state === "BEF") {
        dispatch(setCurrentStep(1));
        navigate(`/installer/${ref}/${archSn}/${archImsi}/work_area_before_install/`);
      } else if (jobCard.state === "UNI") {
        dispatch(setCurrentStep(2));
        navigate(`/installer/${ref}/${archSn}/${archImsi}/UN/`);
      } else if (jobCard.state === "AFT" || jobCard.state === "AFTWA") {
        dispatch(setCurrentStep(3));
        navigate(`/installer/${ref}/${archSn}/${archImsi}/after_install/`);
      } else if (jobCard.state === "REF") {
        navigate(`/installer/${ref}/${archSn}/${archImsi}/job_completed/`);
      }
    } else if (jobCard.install_state === "RE") {
      dispatch(setsteps([0, 1, 2, 3, 4, 5, 6, 7]));
      if (jobCard.state === "BEFWA" || jobCard.state === "BEF") {
        dispatch(setCurrentStep(1));
        navigate(`/installer/${ref}/${archSn}/${archImsi}/work_area_before_install/`);
      } else if (jobCard.state === "UNI") {
        dispatch(setCurrentStep(2));
        navigate(`/installer/${ref}/${archSn}/${archImsi}/RE/`);
      } else if (jobCard.state === "QRCRE") {
        dispatch(setCurrentStep(3));
        navigate(`/installer/${ref}/qrcode/replacement/`);
      } else if (jobCard.state === "DET") {
        dispatch(setCurrentStep(4));
        navigate(`/installer/${ref}/${sn}/${imsi}/details/`);
      } else if (jobCard.state === "AMP") {
        dispatch(setCurrentStep(5));
        navigate(`/installer/${ref}/${sn}/${imsi}/connection_test_interlude/`);
      } else if (jobCard.state === "TES") {
        dispatch(setCurrentStep(6));
        navigate(`/installer/${ref}/${sn}/${imsi}/connection_test/`);
      } else if (jobCard.state === "AFT" || jobCard.state === "AFTWA") {
        dispatch(setCurrentStep(7));
        navigate(`/installer/${ref}/${sn}/${imsi}/after_install/`);
      } else if (jobCard.state === "REF") {
        navigate(`/installer/${ref}/${sn}/${imsi}/job_completed/`);
      }
    }
  };

  return (
    <>
      <ConfirmReplaceUninstallDialog
        open={open}
        handleClose={handleClose}
        type={type}
      />

      {/* Top bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 2,
          pb: 1,
        }}
      >
        <img src="/assets/tjommie-logo.svg" height="24" alt="Tjommie" style={{ display: "block" }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {clockedInTime && (
            <Box sx={{
              display: "flex", alignItems: "center", gap: "5px",
              backgroundColor: "rgba(34,197,94,0.15)",
              border: "1px solid rgba(34,197,94,0.3)",
              borderRadius: "20px",
              px: "10px", py: "4px",
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#22C55E" }} />
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#22C55E" }}>
                Clocked in {clockedInTime}
              </Typography>
            </Box>
          )}
          <LogOutDialog />
          <NotificationIcon />
        </Box>
      </Box>

      {/* Clock-in banner */}
      {!clockedInTime && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            backgroundColor: "#1A1810",
            border: "1px solid rgba(253,185,64,0.2)",
            borderRadius: "12px",
            px: "14px", py: "12px",
            gap: "12px",
          }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", flex: 1 }}>
              <AccessTimeIcon sx={{ color: "#FDB940", fontSize: "1.2rem", mt: "1px", flexShrink: 0 }} />
              <Box>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem" }}>
                  You're not clocked in
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.78rem", mt: "2px" }}>
                  Clock in to access today's jobs and start installing.
                </Typography>
              </Box>
            </Box>
            <Button
              onClick={handleClockIn}
              disableElevation
              sx={{
                backgroundColor: "#FDB940", color: "#000",
                fontWeight: 700, fontSize: "0.82rem",
                px: "14px", py: "7px",
                borderRadius: "8px", textTransform: "none",
                flexShrink: 0,
                "&:hover": { backgroundColor: "#e6a832" },
              }}
            >
              Clock in
            </Button>
          </Box>
        </Box>
      )}

      {/* Greeting */}
      <Box sx={{ px: 2.5, pt: 1, pb: 2 }}>
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            color: "rgba(255,255,255,0.4)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            mb: 0.5,
          }}
        >
          {getGreetingDate()}
        </Typography>
        <Typography
          sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.02em" }}
        >
          Hi, {user_name || "there"}
        </Typography>
      </Box>

      {/* Weak connection warning */}
      {isFullyConnected && isWeakConnection && (
        <Box sx={{ px: 2, mb: 2 }}>
          <Alert severity="warning">
            <strong>Slow Connection</strong> — API response: {responseTime || avgResponseTime}ms
          </Alert>
        </Box>
      )}

      {/* Daily stats row */}
      <Box sx={{ px: 2, mb: 0.5 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <StatTile
              label="Installs today"
              value={isDashboardSuccess ? String(dashboardData.completed_today) : "—"}
              sub={isDashboardSuccess ? `${dashboardData.active_jobs.length} in progress` : ""}
            />
          </Grid>
          <Grid item xs={6}>
            <StatTile
              label="This month"
              value={isDashboardSuccess ? String(dashboardData.completed_month) : "—"}
              sub="completed"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Active in-progress jobs */}
      {isDashboardSuccess && dashboardData.active_jobs.length > 0 && (
        <Box sx={{ px: 2, mt: 2 }}>
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 600,
              color: "rgba(255,255,255,0.4)",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              mb: 1.5,
            }}
          >
            In Progress
          </Typography>
          {dashboardData.active_jobs.map((job) => (
            <ActiveJobCard
              key={job.reference_id}
              job={job}
              onResume={handleResume}
              disabled={!isFullyConnected || !clockedInTime}
            />
          ))}
        </Box>
      )}

      {/* New Installation CTA */}
      <Box sx={{ px: 2, mt: 2, mb: 1 }}>
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 600,
            color: "rgba(255,255,255,0.4)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            mb: 1.5,
          }}
        >
          Installation
        </Typography>
        <Box
          onClick={isFullyConnected && clockedInTime ? handleNew : undefined}
          sx={{
            background: isFullyConnected && clockedInTime
              ? "linear-gradient(135deg, #FDB940 0%, #E09620 100%)"
              : "rgba(253,185,64,0.3)",
            borderRadius: "14px",
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            cursor: isFullyConnected && clockedInTime ? "pointer" : "not-allowed",
            boxShadow: isFullyConnected && clockedInTime ? "0 8px 24px rgba(253,185,64,0.25)" : "none",
            transition: "transform 0.15s",
            "&:active": { transform: isFullyConnected && clockedInTime ? "scale(0.98)" : "none" },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ArticleOutlinedIcon sx={{ color: "#000", fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#000" }}>
              New Installation
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.65)" }}>
              Enter job details to get started
            </Typography>
          </Box>
          <ArrowForwardIcon sx={{ color: "#000", opacity: 0.7 }} />
        </Box>
      </Box>

      {/* Other actions */}
      <Box sx={{ px: 2, mt: 2 }}>
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 600,
            color: "rgba(255,255,255,0.4)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            mb: 1.5,
          }}
        >
          Other Actions
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <ActionTile
              label="Uninstall"
              icon={<WarningAmberIcon sx={{ color: "#ef4444", fontSize: 26 }} />}
              iconBg="rgba(239,68,68,0.12)"
              onClick={isFullyConnected && clockedInTime ? handleUninstall : undefined}
              disabled={!isFullyConnected || !clockedInTime}
            />
          </Grid>
          <Grid item xs={4}>
            <ActionTile
              label="Replacement"
              icon={<SyncAltIcon sx={{ color: "#a78bfa", fontSize: 26 }} />}
              iconBg="rgba(167,139,250,0.12)"
              onClick={isFullyConnected && clockedInTime ? handleReplacement : undefined}
              disabled={!isFullyConnected || !clockedInTime}
            />
          </Grid>
          <Grid item xs={4}>
            <ActionTile
              label="Alerts"
              icon={<AlertsIcon />}
              iconBg="rgba(253,185,64,0.12)"
              onClick={() => navigate("/installer/alerts/")}
              disabled={false}
            />
          </Grid>
        </Grid>
      </Box>

      {/* All submitted/completed jobs */}
      {isDashboardSuccess && dashboardData.recent_cards.length > 0 && (
        <Box sx={{ px: 2, mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 600,
                color: "rgba(255,255,255,0.4)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              My Jobs
            </Typography>
            <Typography
              onClick={() => navigate("/installer/installs/")}
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: "#FDB940",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "2px",
              }}
            >
              View All <ArrowForwardIcon sx={{ fontSize: "0.8rem" }} />
            </Typography>
          </Box>
          {dashboardData.recent_cards.slice(0, 5).map((job) => (
            <RecentJobCard key={job.reference_id} job={job} />
          ))}
        </Box>
      )}

      {/* Spacer so content clears the fixed bottom nav */}
      <Box sx={{ height: "80px" }} />

      {/* Bottom nav */}
      <BottomNav active="home" />

      <ConnectionBlockingDialog
        open={showConnectionDialog}
        connectionDetails={connectionDetails}
        onRetry={handleRetryConnection}
        lastChecked={lastChecked}
      />
    </>
  );
}

// ─── Stat tile ────────────────────────────────────────────────────────────────

function StatTile({ label, value, sub }) {
  return (
    <Box
      sx={{
        backgroundColor: "#161B22",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "12px",
        p: "14px",
      }}
    >
      <Typography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 600,
          color: "rgba(255,255,255,0.45)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          mb: "6px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "1.45rem",
          fontWeight: 700,
          color: "#FDB940",
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: "4px" }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// ─── Active job card with stepper ─────────────────────────────────────────────

export function ActiveJobCard({ job, onResume, disabled }) {
  const { stepIndex, totalSteps, labels } = getStepperInfo(job);
  const typeLabel = getInstallTypeLabel(job.install_state);
  const stateLabel = getStateLabel(job.state);
  const refShort = job.reference_id ? String(job.reference_id).slice(0, 8).toUpperCase() : "—";

  return (
    <Box
      sx={{
        backgroundColor: "#161B22",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "12px",
        p: "16px",
        mb: "10px",
      }}
    >
      {/* Card header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "12px" }}>
        <Box sx={{ flex: 1, mr: 1 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#fff", mb: "2px" }}>
            {job.address || "—"}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", fontFamily: "monospace" }}>
            {refShort}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: "rgba(253,185,64,0.12)",
            color: "#FDB940",
            fontSize: "0.68rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            px: "8px",
            py: "4px",
            borderRadius: "4px",
            whiteSpace: "nowrap",
          }}
        >
          {typeLabel}
        </Box>
      </Box>

      {/* Stepper bar */}
      <Box sx={{ display: "flex", gap: "4px", mb: "6px" }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: "4px",
              borderRadius: "2px",
              backgroundColor:
                i < stepIndex
                  ? "#22C55E"
                  : i === stepIndex
                  ? "#FDB940"
                  : "rgba(255,255,255,0.08)",
            }}
          />
        ))}
      </Box>

      {/* Step labels */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: "14px" }}>
        {labels.map((lbl, i) => (
          <Typography
            key={i}
            sx={{
              fontSize: "0.6rem",
              color:
                i < stepIndex
                  ? "rgba(255,255,255,0.6)"
                  : i === stepIndex
                  ? "#FDB940"
                  : "rgba(255,255,255,0.25)",
              fontWeight: i === stepIndex ? 700 : 400,
            }}
          >
            {lbl}
          </Typography>
        ))}
      </Box>

      {/* Footer: current step label + Resume */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: "10px",
          borderTop: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)" }}>
          {stateLabel}
        </Typography>
        <Button
          onClick={disabled ? undefined : () => onResume(job)}
          disabled={disabled}
          size="small"
          sx={{
            backgroundColor: "rgba(253,185,64,0.14)",
            color: "#FDB940",
            fontWeight: 700,
            fontSize: "0.78rem",
            px: "12px",
            py: "5px",
            borderRadius: "6px",
            textTransform: "none",
            minWidth: 0,
            "&:hover": { backgroundColor: "rgba(253,185,64,0.22)" },
            "&.Mui-disabled": { opacity: 0.4 },
          }}
          startIcon={<PlayArrowIcon sx={{ fontSize: "0.9rem !important" }} />}
        >
          Resume
        </Button>
      </Box>
    </Box>
  );
}

// ─── Recent completed job card ─────────────────────────────────────────────────

export function RecentJobCard({ job }) {
  const refShort = job.reference_id ? String(job.reference_id).slice(0, 8).toUpperCase() : "—";
  const typeLabel = getInstallTypeLabel(job.install_state);
  const isQEP = job.state === "QEP";

  const badgeBg    = isQEP ? "rgba(253,185,64,0.13)"  : "rgba(34,197,94,0.12)";
  const badgeColor = isQEP ? "#FDB940"                 : "#4ADE80";
  const badgeLabel = isQEP ? "QE Review"               : "Completed";

  return (
    <Box
      sx={{
        backgroundColor: "#161B22",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "12px",
        p: "14px 16px",
        mb: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ flex: 1, mr: 1 }}>
        <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#fff", mb: "2px" }}>
          {job.address || "—"}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", fontFamily: "monospace" }}>
          {refShort} · {typeLabel}
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: badgeBg,
          color: badgeColor,
          fontSize: "0.68rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          px: "8px",
          py: "4px",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: "0.85rem" }} />
        {badgeLabel}
      </Box>
    </Box>
  );
}

// ─── Action tile ─────────────────────────────────────────────────────────────

function ActionTile({ label, icon, iconBg, onClick, disabled }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        backgroundColor: "#161B22",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "12px",
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        "&:active": { transform: disabled ? "none" : "scale(0.96)" },
        transition: "transform 0.15s",
        minHeight: 90,
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "10px",
          backgroundColor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#fff", textAlign: "center" }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── Alerts icon ──────────────────────────────────────────────────────────────

function AlertsIcon() {
  const { data: job_card_data, isSuccess } = useGetDeclinedJobCardsQuery();
  const hasAlerts = isSuccess && job_card_data.job_cards.length > 0;
  return (
    <Badge color="error" variant="dot" invisible={!hasAlerts}>
      <NotificationImportantOutlinedIcon sx={{ color: "#FDB940", fontSize: 26 }} />
    </Badge>
  );
}

// ─── Notification icon (top-right bell) ───────────────────────────────────────

function NotificationIcon() {
  let navigate = useNavigate();
  const { data: job_card_data, isSuccess: isGetJobCardDeclinedSuccess } =
    useGetDeclinedJobCardsQuery();
  if (isGetJobCardDeclinedSuccess) {
    const hasAlerts = job_card_data.job_cards.length > 0;
    return (
      <IconButton
        onClick={() => navigate("/installer/alerts/")}
        sx={{
          color: "#FDB940",
          backgroundColor: "rgba(253,185,64,0.1)",
          borderRadius: "50%",
          padding: "8px",
        }}
      >
        <Badge color="error" variant="dot" invisible={!hasAlerts}>
          <NotificationImportantOutlinedIcon fontSize="medium" />
        </Badge>
      </IconButton>
    );
  } else return <></>;
}

// ─── Bottom navigation bar ────────────────────────────────────────────────────

export function BottomNav({ active }) {
  let navigate = useNavigate();

  const navItems = [
    {
      key: "home",
      label: "Home",
      icon: <HomeIcon sx={{ fontSize: 22 }} />,
      onClick: () => navigate("/installer/"),
    },
    {
      key: "installs",
      label: "Installs",
      icon: <WorkOutlineIcon sx={{ fontSize: 22 }} />,
      onClick: () => navigate("/installer/installs/"),
    },
    {
      key: "alerts",
      label: "Alerts",
      icon: <NotificationAlertIcon />,
      onClick: () => navigate("/installer/alerts/"),
    },
  ];

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "rgba(13,17,23,0.95)",
        backdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        justifyContent: "space-around",
        py: 1,
        pb: "env(safe-area-inset-bottom, 8px)",
        zIndex: 100,
      }}
    >
      {navItems.map((item) => (
        <Box
          key={item.key}
          onClick={item.onClick}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "2px",
            px: 2,
            py: 0.5,
            cursor: "pointer",
            color: active === item.key ? "#FDB940" : "rgba(255,255,255,0.4)",
          }}
        >
          {item.icon}
          <Typography sx={{ fontSize: "0.65rem", fontWeight: active === item.key ? 700 : 400 }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function NotificationAlertIcon() {
  const { data, isSuccess } = useGetDeclinedJobCardsQuery();
  const hasAlerts = isSuccess && data.job_cards.length > 0;
  return (
    <Badge color="error" variant="dot" invisible={!hasAlerts}>
      <NotificationsIcon sx={{ fontSize: 22 }} />
    </Badge>
  );
}

export default React.memo(InstallerLandingPage);
